import React from 'react';
import { useAtom } from 'jotai';
import { Checkbox } from '@carbon/react';
import { CollapsibleSection } from 'components/map/collapsible-section';
import { subsurfaceScopedLayersVisibilityAtomFamily } from './scoped-layers-atoms';
import styles from './subsurface-explorer-scoped-layers.module.scss';

/*
 * Scoped-layer panel for the Subsurface Explorer inset map (INS-8658 / INS-9182).
 *
 * Groups:
 *   - "Subsurface Explorer Wells" → Nearby Wells / Offset Wells (INS-9182).
 *   - "Wells" → Subject — the subject well (mirrors the main Well Page map).
 *   - "Development Areas" → Intersecting Units (mirrors the main Well Page map).
 *
 * Visibility is per-subject via `subsurfaceScopedLayersVisibilityAtomFamily`, the
 * inset's OWN atoms — independent of the main Well Page map (so toggles here
 * never affect the main map, and vice versa).
 *
 * Offset is disabled when `hasOffset` is false (no completed spacing job);
 * Intersecting Units is disabled when `hasUnits` is false (no intersecting units).
 * No per-row color swatch (consistent with the existing rows).
 */
export const SubsurfaceExplorerScopedLayers: React.FC<{
  uwi: string;
  hasOffset: boolean;
  hasUnits: boolean;
}> = ({ uwi, hasOffset, hasUnits }) => {
  const [visibility, setVisibility] = useAtom(
    subsurfaceScopedLayersVisibilityAtomFamily(uwi)
  );

  const nearbyChecked = visibility.nearby;
  // A missing offset URL / no units means there's nothing to show — treat as off.
  const offsetChecked = hasOffset && visibility.offset;
  const subjectChecked = visibility.subject;
  const unitsChecked = hasUnits && visibility.units;

  const allSseChecked = nearbyChecked && offsetChecked;
  const someSseChecked = nearbyChecked || offsetChecked;
  const sseIndeterminate = someSseChecked && !allSseChecked;

  const handleSseGroupToggle = (
    _evt: React.ChangeEvent<HTMLInputElement>,
    { checked }: { checked: boolean }
  ) => {
    setVisibility((prev) => ({
      ...prev,
      nearby: checked,
      offset: hasOffset ? checked : false,
    }));
  };

  return (
    <>
      {/* Wells → Subject — mirrors the main Well Page scoped panel (black swatch). */}
      <CollapsibleSection
        id="scoped-wells"
        title="Wells"
        checked={subjectChecked}
        onChange={(_evt, { checked }) =>
          setVisibility((prev) => ({ ...prev, subject: checked }))
        }
        defaultOpen={true}
      >
        <div className={styles.subjectSwatch}>
          <Checkbox
            id="subject-visibility"
            labelText="Subject"
            checked={subjectChecked}
            onChange={() =>
              setVisibility((prev) => ({ ...prev, subject: !prev.subject }))
            }
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        id="scoped-development-areas"
        title="Development Areas"
        checked={unitsChecked}
        onChange={(_evt, { checked }) =>
          setVisibility((prev) => ({ ...prev, units: checked }))
        }
        defaultOpen={true}
        disabled={!hasUnits}
      >
        <div className={styles.scopedLayersContainer}>
          <Checkbox
            id="units-visibility"
            labelText="Intersecting Units"
            disabled={!hasUnits}
            checked={unitsChecked}
            onChange={() =>
              setVisibility((prev) => ({ ...prev, units: !prev.units }))
            }
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        id="scoped-subsurface-explorer-wells"
        title="Subsurface Explorer Wells"
        checked={allSseChecked}
        indeterminate={sseIndeterminate}
        onChange={handleSseGroupToggle}
        defaultOpen={true}
      >
        <div className={styles.scopedLayersContainer}>
          <Checkbox
            id="nearby-wells-visibility"
            labelText="Nearby Wells"
            checked={nearbyChecked}
            onChange={() =>
              setVisibility((prev) => ({ ...prev, nearby: !prev.nearby }))
            }
          />
          <Checkbox
            id="offset-wells-visibility"
            labelText="Offset Wells"
            disabled={!hasOffset}
            checked={offsetChecked}
            onChange={() =>
              setVisibility((prev) => ({ ...prev, offset: !prev.offset }))
            }
          />
        </div>
      </CollapsibleSection>
    </>
  );
};
